import * as React from 'react';

/**
 * Props for the List component
 */
export interface ListProps {
  /**
   * List items (typically List.Item components)
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes for the list
   */
  className?: string;
  /**
   * Whether to show dividers between items
   * @default false
   */
  dividers?: boolean;
}

/**
 * Props for List.Item component
 */
export interface ListItemProps {
  /**
   * Item content
   */
  children: React.ReactNode;
  /**
   * Content to show on the left side (icon, avatar, etc.)
   */
  leading?: React.ReactNode;
  /**
   * Content to show on the right side (action button, text, etc.)
   */
  trailing?: React.ReactNode;
  /**
   * Whether the item is selected
   */
  selected?: boolean;
  /**
   * Whether the item is disabled
   */
  disabled?: boolean;
  /**
   * Click handler for the item
   */
  onClick?: () => void;
  /**
   * Additional CSS classes for the item
   */
  className?: string;
}

// Context for passing list state to compound components
interface ListContextValue {
  dividers: boolean;
}

const ListContext = React.createContext<ListContextValue | null>(null);

function useListContext(): ListContextValue {
  const context = React.useContext(ListContext);
  if (!context) {
    throw new Error('List compound components must be used within List');
  }
  return context;
}

/**
 * List Item component
 *
 * When interactive (has onClick), renders as a button for proper accessibility.
 * When non-interactive, renders as a plain li element.
 */
const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  (
    {
      children,
      leading,
      trailing,
      selected = false,
      disabled = false,
      onClick,
      className = '',
    },
    ref
  ) => {
    const { dividers } = useListContext();
    const isInteractive = !!onClick;

    const baseContentClasses = [
      // Base styles
      'flex items-center w-full',
      'min-h-[48px]',
      'px-3 py-2', // 12px horizontal, 8px vertical
      'text-sm',
      'text-secondary-700',
      'transition-colors duration-150 ease-out',
      'text-left', // Ensure text alignment for button
    ];

    const interactiveClasses = [
      ...baseContentClasses,
      // Interactive states
      !disabled && 'cursor-pointer',
      !disabled && !selected && 'hover:bg-secondary-50',
      // Selected state
      selected && 'bg-primary-50 border-l-[3px] border-l-primary-600',
      selected && 'pl-[9px]', // Adjust padding for left border (12px - 3px)
      // Disabled state
      disabled && 'opacity-50 cursor-not-allowed',
      // Focus styles for keyboard navigation
      !disabled && 'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 focus-visible:ring-inset',
    ]
      .filter(Boolean)
      .join(' ');

    const nonInteractiveClasses = [
      ...baseContentClasses,
      // Dividers (for non-interactive items, applied to li directly)
      dividers && 'border-b border-secondary-200',
      dividers && 'last:border-b-0',
      // Selected state (visual only for non-interactive)
      selected && 'bg-primary-50 border-l-[3px] border-l-primary-600',
      selected && 'pl-[9px]', // Adjust padding for left border (12px - 3px)
      // Disabled state (visual only for non-interactive)
      disabled && 'opacity-50',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const liClasses = [
      // Dividers
      dividers && 'border-b border-secondary-200',
      dividers && 'last:border-b-0',
      // For interactive items, add className to li
      isInteractive && className,
    ]
      .filter(Boolean)
      .join(' ');

    const content = (
      <>
        {leading && (
          <span className="flex-shrink-0 mr-3 flex items-center justify-center text-secondary-500">
            {leading}
          </span>
        )}
        <span className="flex-1 min-w-0">{children}</span>
        {trailing && (
          <span className="flex-shrink-0 ml-3 flex items-center justify-center text-secondary-500">
            {trailing}
          </span>
        )}
      </>
    );

    if (isInteractive) {
      return (
        <li ref={ref} className={liClasses || undefined}>
          <button
            type="button"
            className={interactiveClasses}
            onClick={onClick}
            disabled={disabled}
            aria-pressed={selected}
          >
            {content}
          </button>
        </li>
      );
    }

    return (
      <li ref={ref} className={nonInteractiveClasses}>
        {content}
      </li>
    );
  }
);

ListItem.displayName = 'List.Item';

/**
 * List component for displaying vertical lists of items.
 *
 * Designed for insurance industry applications where clear organization
 * of related items is critical for navigation, settings, and data display.
 *
 * Features:
 * - Leading content (icons, avatars)
 * - Trailing content (actions, badges)
 * - Selected state with visual indicator
 * - Disabled state
 * - Optional dividers between items
 * - Keyboard navigation support
 * - Proper accessibility with ARIA attributes
 *
 * @example
 * // Basic list
 * <List>
 *   <List.Item>First item</List.Item>
 *   <List.Item>Second item</List.Item>
 *   <List.Item>Third item</List.Item>
 * </List>
 *
 * @example
 * // List with icons and actions
 * <List dividers>
 *   <List.Item
 *     leading={<User className="w-5 h-5" />}
 *     trailing={<ChevronRight className="w-4 h-4" />}
 *     onClick={() => navigate('/profile')}
 *   >
 *     Profile Settings
 *   </List.Item>
 *   <List.Item
 *     leading={<Bell className="w-5 h-5" />}
 *     trailing={<Badge>3</Badge>}
 *     onClick={() => navigate('/notifications')}
 *   >
 *     Notifications
 *   </List.Item>
 * </List>
 *
 * @example
 * // List with selection
 * <List>
 *   <List.Item selected onClick={() => setSelected('dashboard')}>
 *     Dashboard
 *   </List.Item>
 *   <List.Item onClick={() => setSelected('policies')}>
 *     Policies
 *   </List.Item>
 * </List>
 */
const ListRoot = React.forwardRef<HTMLUListElement, ListProps>(
  ({ children, className = '', dividers = false }, ref) => {
    const contextValue = React.useMemo<ListContextValue>(
      () => ({
        dividers,
      }),
      [dividers]
    );

    const listClasses = ['', className].filter(Boolean).join(' ');

    return (
      <ListContext.Provider value={contextValue}>
        <ul ref={ref} className={listClasses || undefined}>
          {children}
        </ul>
      </ListContext.Provider>
    );
  }
);

ListRoot.displayName = 'List';

/**
 * List compound component with Item
 */
export const List = Object.assign(ListRoot, {
  Item: ListItem,
});
